import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent } from "../../../components/ui/card";
import { ConversionFunnelWidget } from "./ConversionFunnelWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { id: "today", label: "Oggi" },
  { id: "7d", label: "7 giorni" },
  { id: "30d", label: "30 giorni" },
  { id: "90d", label: "90 giorni" },
];

// Only metrics NOT already present elsewhere in the dashboard.
// (Visitatori / Pageviews / Ordini are shown in the upper KPI cards + Trend Visite.)
const KPI_CONFIG = [
  { key: "cart_adds", label: "Carrelli", color: "text-orange-400", icon: "🛒" },
  { key: "checkouts", label: "Checkout", color: "text-purple-400", icon: "💳" },
  { key: "conversion", label: "Conv.", color: "text-red-400", icon: "📈", suffix: "%" },
];

const ProgressBar = ({ value, max, color }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="w-full bg-gray-800/50 rounded-full h-1.5 mt-1">
      <div className={`${color} h-1.5 rounded-full transition-all duration-500`} style={{ width: `${Math.max(pct, 1)}%` }} />
    </div>
  );
};

const REFERRER_MAP = [
  { match: /(^|\.)google\./i, label: "🔎 Google", source: "Google Search" },
  { match: /(^|\.)bing\./i, label: "🔎 Bing", source: "Bing Search" },
  { match: /(^|\.)duckduckgo\./i, label: "🦆 DuckDuckGo", source: "DuckDuckGo" },
  { match: /(^|\.)yahoo\./i, label: "🔎 Yahoo", source: "Yahoo Search" },
  { match: /(^|\.)ecosia\./i, label: "🌱 Ecosia", source: "Ecosia" },
  { match: /(^|\.)facebook\./i, label: "📘 Facebook", source: "Facebook" },
  { match: /(^|\.)fb\./i, label: "📘 Facebook", source: "Facebook" },
  { match: /(^|\.)instagram\./i, label: "📸 Instagram", source: "Instagram" },
  { match: /(^|\.)l\.instagram\./i, label: "📸 Instagram", source: "Instagram" },
  { match: /(^|\.)tiktok\./i, label: "🎵 TikTok", source: "TikTok" },
  { match: /(^|\.)youtube\./i, label: "▶️ YouTube", source: "YouTube" },
  { match: /(^|\.)youtu\.be$/i, label: "▶️ YouTube", source: "YouTube" },
  { match: /(^|\.)twitter\./i, label: "🐦 Twitter / X", source: "Twitter" },
  { match: /(^|\.)x\.com$/i, label: "🐦 Twitter / X", source: "Twitter" },
  { match: /(^|\.)t\.co$/i, label: "🐦 Twitter / X", source: "Twitter" },
  { match: /(^|\.)linkedin\./i, label: "💼 LinkedIn", source: "LinkedIn" },
  { match: /(^|\.)pinterest\./i, label: "📌 Pinterest", source: "Pinterest" },
  { match: /(^|\.)reddit\./i, label: "👽 Reddit", source: "Reddit" },
  { match: /(^|\.)whatsapp\./i, label: "💬 WhatsApp", source: "WhatsApp" },
  { match: /(^|\.)wa\.me$/i, label: "💬 WhatsApp", source: "WhatsApp" },
  { match: /(^|\.)telegram\./i, label: "✈️ Telegram", source: "Telegram" },
  { match: /(^|\.)t\.me$/i, label: "✈️ Telegram", source: "Telegram" },
  { match: /(^|\.)ebay\./i, label: "🛒 eBay", source: "eBay" },
  { match: /(^|\.)amazon\./i, label: "📦 Amazon", source: "Amazon" },
  { match: /(^|\.)edicolavillaricca\./i, label: "🏠 Sito stesso", source: "Navigazione interna" },
];

const formatReferrer = (raw) => {
  // Direct traffic: empty, null, undefined, "-", "direct", or just a dash
  const val = (raw ?? "").toString().trim();
  if (!val || val === "-" || val === "—" || val.toLowerCase() === "direct" || val.toLowerCase() === "(direct)") {
    return {
      label: "🚪 Traffico Diretto",
      subtitle: "Bookmark, URL digitato o app senza referrer",
    };
  }
  // Try to extract hostname
  let host = val;
  try {
    if (val.startsWith("http://") || val.startsWith("https://") || val.startsWith("//")) {
      const u = new URL(val.startsWith("//") ? `https:${val}` : val);
      host = u.hostname;
    } else if (val.includes("/")) {
      host = val.split("/")[0];
    }
  } catch {
    host = val;
  }
  host = host.replace(/^www\./i, "").toLowerCase();
  // Lookup known sources
  const known = REFERRER_MAP.find((r) => r.match.test(host));
  if (known) {
    return { label: known.label, subtitle: known.source };
  }
  // Unknown: just show the cleaned domain
  return { label: `🌐 ${host}`, subtitle: val };
};

const formatPagePath = (path) => {
  if (!path) return { label: "—", subtitle: null };
  if (path === "/") return { label: "🏠 Home", subtitle: path };
  if (path === "/prodotti") return { label: "📦 Catalogo prodotti", subtitle: path };
  if (path === "/checkout") return { label: "💳 Checkout", subtitle: path };
  if (path === "/area-personale") return { label: "👤 Area personale", subtitle: path };
  if (path.startsWith("/admin") || path.startsWith("/gestione"))
    return { label: "🔧 Admin", subtitle: path };
  if (path.startsWith("/prodotto/")) {
    const id = path.replace("/prodotto/", "").split("?")[0];
    return { label: `🛒 Prodotto · ${id.slice(0, 8)}…`, subtitle: path };
  }
  const m = path.match(/\?article=([a-f0-9-]+)/i);
  if (m) {
    return { label: `🛒 Prodotto · ${m[1].slice(0, 8)}…`, subtitle: path };
  }
  // Strip long query strings to last 40 chars
  if (path.length > 50) {
    return { label: `${path.slice(0, 50)}…`, subtitle: path };
  }
  return { label: path, subtitle: null };
};

const ListWidget = ({ title, icon, items, valueKey = "count", labelKey = "_id", barColor = "bg-orange-500", emptyMsg = "Nessun dato ancora", formatLabel = null }) => {
  const max = useMemo(() => Math.max(...items.map((i) => i[valueKey] || 0), 1), [items, valueKey]);
  return (
    <Card className="bg-[#0a0a0f] border border-white/10 text-gray-200">
      <CardContent className="p-6">
        <h3 className="text-sm font-mono uppercase tracking-wider text-amber-400 mb-4 flex items-center gap-2">
          <span>{icon}</span> {title}
        </h3>
        {items.length === 0 ? (
          <p className="text-gray-500 text-xs text-center py-6">{emptyMsg}</p>
        ) : (
          <div className="space-y-2">
            {items.map((item, idx) => {
              const rawLabel = item[labelKey];
              const formatted = formatLabel ? formatLabel(rawLabel) : { label: rawLabel || "—", subtitle: null };
              return (
                <div key={rawLabel ?? `row-${idx}`} data-testid={`analytics-row-${idx}`}>
                  <div className="flex justify-between text-xs gap-2">
                    <span className="font-mono text-gray-300 truncate" title={formatted.subtitle || rawLabel || "—"}>
                      {formatted.label}
                    </span>
                    <span className="font-mono text-gray-100 whitespace-nowrap">{item[valueKey]}</span>
                  </div>
                  <ProgressBar value={item[valueKey] || 0} max={max} color={barColor} />
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const AnalyticsAdvanced = ({ formatPrice }) => {
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/dashboard/analytics-full?period=${period}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        console.error("Analytics fetch error", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [period]);

  // Aggregate referrers by their mapped label so multiple URLs from the
  // same platform (e.g. google.com/search?q=... + www.google.com) collapse
  // into a single row. (Must run before any early return to keep hooks order stable.)
  // NOTE: backend returns rows as {referrer, count}, NOT {_id, count}.
  const aggregatedReferrers = useMemo(() => {
    const raw = data?.top_referrers || [];
    const buckets = new Map();
    for (const item of raw) {
      const rawValue = item.referrer ?? item._id; // backend uses `referrer`
      const formatted = formatReferrer(rawValue);
      const key = formatted.label;
      const prev = buckets.get(key) || {
        _id: key,
        count: 0,
        _subtitle: formatted.subtitle,
        _examples: [],
      };
      prev.count += item.count || 0;
      if (rawValue && prev._examples.length < 3 && !prev._examples.includes(rawValue)) {
        prev._examples.push(rawValue);
      }
      buckets.set(key, prev);
    }
    return Array.from(buckets.values()).sort((a, b) => b.count - a.count);
  }, [data?.top_referrers]);

  if (loading && !data) {
    return (
      <div className="text-center py-8" data-testid="analytics-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
      </div>
    );
  }

  const kpi = data?.kpi || {};
  const peakMaxCount = Math.max(...(data?.peak_hours || []).map((h) => h.count), 1);

  return (
    <section className="mt-10 -mx-4 md:mx-0" data-testid="analytics-advanced">
      <div className="bg-[#0a0a0f] rounded-xl border border-white/10 p-4 md:p-6 space-y-6">
        {/* Header + period selector */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-lg font-mono uppercase tracking-wider text-amber-400">
              📊 Funnel Conversione & Engagement
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Carrelli, prodotti, pagine, referrer e carrelli abbandonati
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono uppercase text-gray-500">Periodo:</span>
            <div className="flex gap-1 bg-black/40 p-1 rounded-lg border border-white/5">
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPeriod(p.id)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase rounded transition-colors ${
                    period === p.id
                      ? "bg-amber-400 text-black font-bold"
                      : "text-gray-400 hover:text-amber-400"
                  }`}
                  data-testid={`period-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* KPI strip — only the 3 metrics NOT already in upper dashboard */}
        <div className="grid grid-cols-3 gap-3" data-testid="analytics-kpi-grid">
          {KPI_CONFIG.map((k) => (
            <div
              key={k.key}
              className="bg-black/40 border border-white/5 rounded-lg p-4"
              data-testid={`kpi-${k.key}`}
            >
              <p className="text-xs font-mono uppercase text-gray-500 mb-1">
                {k.icon} {k.label}
              </p>
              <p className={`text-2xl md:text-3xl font-bold ${k.color}`}>
                {(kpi[k.key] ?? 0).toLocaleString("it-IT")}{k.suffix || ""}
              </p>
            </div>
          ))}
        </div>

        {/* Peak hours + Top products */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-[#0a0a0f] border border-white/10 text-gray-200">
            <CardContent className="p-6">
              <h3 className="text-sm font-mono uppercase tracking-wider text-amber-400 mb-4">
                🕐 Orari di Picco
              </h3>
              {!data?.peak_hours?.length ? (
                <p className="text-gray-500 text-xs text-center py-8">Dati insufficienti</p>
              ) : (
                <div className="flex items-end justify-between gap-0.5 h-32">
                  {Array.from({ length: 24 }).map((_, h) => {
                    const found = data.peak_hours.find((x) => x.hour === h);
                    const count = found?.count || 0;
                    const pct = (count / peakMaxCount) * 100;
                    return (
                      <div
                        key={`hour-${h}`}
                        className="flex-1 flex flex-col items-center justify-end h-full group"
                        title={`${String(h).padStart(2, "0")}:00 — ${count} eventi`}
                      >
                        <div
                          className="w-full bg-cyan-500 hover:bg-cyan-400 rounded-t transition-colors"
                          style={{ height: `${Math.max(pct, 2)}%` }}
                        />
                        {h % 3 === 0 && (
                          <span className="text-[10px] font-mono text-gray-600 mt-1">{String(h).padStart(2, "0")}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <ListWidget
            title="Prodotti più Visti"
            icon="🏆"
            items={(data?.top_products || []).map((p) => ({
              _id: p.title,
              count: `${p.views || 0} viste · ${p.cart_adds || 0} 🛒`,
            }))}
            valueKey="count"
            barColor="bg-cyan-400"
            emptyMsg="Nessuna visualizzazione prodotti"
          />
        </div>

        {/* Pages + Referrer */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ListWidget
            title="Pagine più Visitate"
            icon="🔗"
            items={data?.top_pages || []}
            labelKey="path"
            barColor="bg-orange-400"
            emptyMsg="Inizia a navigare il sito per popolare i dati"
            formatLabel={formatPagePath}
          />
          <ListWidget
            title="Da Dove Arrivano (Referrer)"
            icon="🔗"
            items={aggregatedReferrers}
            labelKey="_id"
            barColor="bg-green-400"
            emptyMsg="Nessun referrer esterno ancora"
          />
        </div>

        {/* Conversion Funnel */}
        <ConversionFunnelWidget period={period} />

        {/* Abandoned carts table */}
        <Card className="bg-[#0a0a0f] border border-yellow-400/30 text-gray-200">
          <CardContent className="p-6">
            <h3 className="text-sm font-mono uppercase tracking-wider text-yellow-400 mb-4 flex items-center gap-2">
              🛒 Carrelli Abbandonati ({data?.abandoned_carts?.length || 0})
            </h3>
            {!data?.abandoned_carts?.length ? (
              <p className="text-gray-500 text-xs text-center py-6">Nessun carrello abbandonato nel periodo</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="abandoned-carts-table">
                  <thead className="text-gray-500 font-mono uppercase">
                    <tr>
                      <th className="text-left py-2 pr-3">Aggiornato</th>
                      <th className="text-left py-2 pr-3">Email</th>
                      <th className="text-left py-2 pr-3">Paese</th>
                      <th className="text-left py-2 pr-3">Articoli</th>
                      <th className="text-right py-2 pr-3">Totale</th>
                      <th className="text-left py-2 pr-3">Stato</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {data.abandoned_carts.map((c, i) => (
                      <tr key={c.email || `cart-${c.last_seen_at || i}`} className="hover:bg-white/5">
                        <td className="py-2 pr-3 font-mono text-gray-400">
                          {c.last_seen_at ? new Date(c.last_seen_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </td>
                        <td className="py-2 pr-3 text-gray-200">{c.email || "— ospite —"}</td>
                        <td className="py-2 pr-3 text-gray-300">{c.country || ""}</td>
                        <td className="py-2 pr-3 text-gray-300">{c.items_count} prodott{c.items_count === 1 ? "o" : "i"}</td>
                        <td className="py-2 pr-3 text-right font-bold text-yellow-400">
                          {formatPrice ? formatPrice(c.cart_total || 0) : `€${(c.cart_total || 0).toFixed(2)}`}
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase ${c.reminder_sent ? "bg-green-900 text-green-300" : "bg-yellow-900 text-yellow-300"}`}>
                            {c.reminder_sent ? "Reminder Inviato" : "Carrello Attivo"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
